/**
 * AddPaymentMethod - Component for adding new payment methods
 *
 * Supports:
 * - Credit/Debit cards via Stripe Elements
 * - PayPal via redirect flow
 */

import { useState } from 'react'
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { getApiUrl } from '../../api/client'
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { CreditCard, X, ArrowLeft, Loader2 } from 'lucide-react'

// API_URL is computed dynamically via getApiUrl() for org-scoped subdomains
const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

// Initialize Stripe outside component to avoid recreating on each render
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null

interface AddPaymentMethodProps {
  allowedTypes?: string[]
  onSuccess: () => void
  onCancel: () => void
}

// Card form using Stripe Elements
function CardForm({
  onSuccess,
  onBack,
}: {
  onSuccess: () => void
  onBack: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }

      // Create payment method with Stripe
      const { paymentMethod, error: stripeError } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        })

      if (stripeError) {
        setError(stripeError.message || 'Failed to process card')
        return
      }

      if (!paymentMethod) {
        setError('Failed to create payment method')
        return
      }

      // Send to our backend
      const response = await fetch(`${getApiUrl()}/api/me/payment-methods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          processor: 'stripe',
          processor_token: paymentMethod.id,
          set_as_default: true,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Failed to add card')
      }

      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-[var(--text-secondary)]">
        Enter your card details:
      </p>

      {error && (
        <div className="rounded-lg bg-[var(--error-muted)]/30 p-3 text-sm text-[var(--error)]">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1f2937',
                '::placeholder': {
                  color: '#9ca3af',
                },
              },
              invalid: {
                color: '#ef4444',
              },
            },
          }}
        />
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
          disabled={loading}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button type="submit" className="flex-1" disabled={loading || !stripe}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding...
            </>
          ) : (
            'Add Card'
          )}
        </Button>
      </div>
    </form>
  )
}

// PayPal form (redirect flow)
function PayPalForm({
  onBack,
}: {
  onSuccess: () => void
  onBack: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConnect() {
    setLoading(true)
    setError(null)

    try {
      // Create PayPal setup token via backend
      const response = await fetch(`${getApiUrl()}/api/me/billing/paypal/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          return_url: window.location.href + '?paypal_success=true',
          cancel_url: window.location.href + '?paypal_cancelled=true',
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Failed to start PayPal connection')
      }

      const data = await response.json()

      if (data.approve_url) {
        // Store the current URL to redirect back after PayPal callback
        sessionStorage.setItem('paypal_return_url', window.location.href)
        // Redirect to PayPal for approval
        window.location.href = data.approve_url
      } else {
        throw new Error('No PayPal approval URL returned')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect PayPal')
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--text-secondary)]">
        You'll be redirected to PayPal to authorize u.software to charge your
        account.
      </p>

      {error && (
        <div className="rounded-lg bg-[var(--error-muted)]/30 p-3 text-sm text-[var(--error)]">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
          disabled={loading}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          type="button"
          className="flex-1 bg-[#003087] hover:bg-[#002670]"
          onClick={handleConnect}
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Connecting...
            </>
          ) : (
            'Connect PayPal'
          )}
        </Button>
      </div>
    </div>
  )
}

// Main component
export function AddPaymentMethod({
  allowedTypes = ['card', 'paypal'],
  onSuccess,
  onCancel,
}: AddPaymentMethodProps) {
  const [methodType, setMethodType] = useState<'card' | 'paypal' | null>(null)

  const elementsOptions: StripeElementsOptions = {
    mode: 'setup',
    currency: 'usd',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="mx-4 max-w-md p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">
            Add Payment Method
          </h3>
          <button
            onClick={onCancel}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1: Choose payment method type */}
        {!methodType && (
          <div className="space-y-3">
            <p className="mb-4 text-sm text-[var(--text-secondary)]">
              Select how you want to pay:
            </p>

            {allowedTypes.includes('card') && STRIPE_KEY && (
              <button
                onClick={() => setMethodType('card')}
                className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
              >
                <CreditCard className="h-6 w-6 text-[var(--text-secondary)]" />
                <div>
                  <span className="font-medium text-[var(--text-primary)]">
                    Credit or Debit Card
                  </span>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Visa, Mastercard, American Express
                  </p>
                </div>
              </button>
            )}

            {allowedTypes.includes('paypal') && (
              <button
                onClick={() => setMethodType('paypal')}
                className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded bg-[#003087] text-xs font-bold text-white">
                  P
                </div>
                <div>
                  <span className="font-medium text-[var(--text-primary)]">
                    PayPal
                  </span>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Pay with your PayPal account
                  </p>
                </div>
              </button>
            )}

            {!STRIPE_KEY && allowedTypes.includes('card') && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Card payments require Stripe configuration.
              </p>
            )}
          </div>
        )}

        {/* Step 2: Card form with Stripe Elements */}
        {methodType === 'card' && stripePromise && (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <CardForm
              onSuccess={onSuccess}
              onBack={() => setMethodType(null)}
            />
          </Elements>
        )}

        {/* Step 2: PayPal redirect flow */}
        {methodType === 'paypal' && (
          <PayPalForm
            onSuccess={onSuccess}
            onBack={() => setMethodType(null)}
          />
        )}
      </Card>
    </div>
  )
}
